/**
 * ANALYTICS
 * =========
 * One function — logEvent() — that the whole app uses to track events.
 *
 * Every event is described by four client-decided fields:
 *   - section   the feed section (HOROSCOPE_PAGE, or a widget's API `type`)
 *   - action    open | close | view | click
 *   - component the specific part acted on (e.g. lucky_number, cta) — optional
 *   - meta      any extra data; serialized to a JSON string in the envelope
 *
 * On top of those it builds the standard ShareChat event envelope (mirrored from
 * vibely-web's utils/logEvents.ts): the numeric event id (`i`), identity
 * (userId / deviceId / sessionId / referrer from the cookie layer), and the
 * app/client/tenant constants. Then it hands the payload to the transport in
 * api/events.ts. Fire-and-forget: it never throws.
 *
 * Usage:
 *   import { logEvent } from './lib/analytics'
 *   import { EventSection, EventAction } from './lib/eventConstants'
 *   logEvent({ section: EventSection.HOROSCOPE_PAGE, action: EventAction.OPEN })
 *
 * ⚠️ The exact envelope contract is mirrored from vibely-web and must be
 * confirmed against the askk events API (see eventConstants.ts).
 */
import { getAppVersion, getAuthContext } from './cookies'
import { sendEvents, SendOptions } from '../api/events'
import {
  APP_NAME,
  BROKER_NAME,
  CLIENT_NAME,
  CLIENT_TYPE,
  EventActionName,
  HOROSCOPE_EVENT_ID,
  TENANT,
} from './eventConstants'

export interface AppEvent {
  /** Feed section: HOROSCOPE_PAGE or a widget's API `type`. */
  section: string
  action: EventActionName
  /** The specific part acted on (e.g. lucky_number, cta). */
  component?: string
  /** Extra event-specific data, serialized to a JSON string in the envelope. */
  meta?: Record<string, unknown>
  referrer?: string
  /** Any other top-level event fields (e.g. mediatype) get merged in. */
  [key: string]: unknown
}

/** Best-effort unique id for an event. */
function eventUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `${Date.now()}-${Math.floor(Math.random() * 1e9)}`
}

type AuthContext = ReturnType<typeof getAuthContext>

/** Build one `requestArray` item (each event gets its own uuid + timestamps). */
function buildEventItem(event: AppEvent, auth: AuthContext, appVersion: number | undefined) {
  const { section, action, component, meta, referrer, ...rest } = event
  const now = Date.now()
  return {
    i: HOROSCOPE_EVENT_ID,
    section,
    action,
    component,
    meta: meta ? JSON.stringify(meta) : undefined,
    tenant: TENANT,
    appName: APP_NAME,
    appV: appVersion,
    client: CLIENT_NAME,
    clientType: CLIENT_TYPE,
    userId: auth.userId ?? undefined,
    deviceId: auth.deviceId ?? undefined,
    sessionId: auth.sessionId ?? undefined,
    referrer: referrer ?? auth.referrer ?? undefined,
    eventUuid: eventUuid(),
    domainUrl: typeof window !== 'undefined' ? window.location.origin : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    t: now,
    ntp_eventRecordTime: now,
    ntp_eventDispatchTime: now,
    ...rest,
  }
}

/**
 * Send one or more events in a SINGLE batched POST (the API's `batchRequest` /
 * `requestArray` is built for this). Use this when events fire together — e.g. a
 * CTA click + page close right before a redirect — so they share one round-trip
 * instead of racing two requests against the WebView teardown.
 *
 * Returns the send promise so a caller about to destroy the page can await it.
 * sendEvents never rejects, so the promise always resolves.
 *
 * Pass `{ keepalive: true }` only from teardown paths (CTA redirect, page close)
 * — see SendOptions. Live-page sends should omit it.
 */
export function logEvents(events: AppEvent[], opts?: SendOptions): Promise<void> {
  if (events.length === 0) return Promise.resolve()
  const auth = getAuthContext()
  const appVersion = getAppVersion()

  const payload = {
    userId: auth.userId ?? undefined,
    deviceId: auth.deviceId ?? undefined,
    client: CLIENT_NAME,
    appVersion,
    bn: BROKER_NAME,
    message: {
      batchRequest: '1',
      requestArray: events.map((e) => buildEventItem(e, auth, appVersion)),
    },
  }

  return sendEvents(payload, opts)
}

/** Send a single event (a one-item batch). */
export function logEvent(event: AppEvent, opts?: SendOptions): Promise<void> {
  return logEvents([event], opts)
}

/**
 * Wait for in-flight event sends to flush before doing something that destroys
 * the page (a bridge navigate that closes the WebView). Capped by a timeout so a
 * slow/failing network can never block the user's redirect — past the cap we let
 * the request finish best-effort via fetch `keepalive`.
 */
export function flushEvents(sends: Promise<void>[], timeoutMs = 500): Promise<void> {
  const all = Promise.all(sends).then(() => undefined)
  const cap = new Promise<void>((resolve) => setTimeout(resolve, timeoutMs))
  return Promise.race([all, cap])
}
